import { defineStore } from 'pinia';
import ChatbotsAPI from 'dashboard/api/chatbots';
import CustomAutomationsAPI from 'dashboard/api/customAutomations';

// Chatbots — backed by the Rails API (/api/v1/accounts/:id/chatbots).
// The model stores `name` + a freeform `config` jsonb; the view-model keeps the
// flat shape the cards/dialog use (botName + the config fields spread).

const toViewModel = bot => ({
  id: bot.id,
  active: bot.active,
  botName: bot.name,
  botUserId: bot.bot_user_id,
  botUser: bot.bot_user,
  ...(bot.config || {}),
});

const toPayload = ({ botName, active, botUserId, botUser, ...config }) => {
  delete config.id;
  return { name: botName, active, bot_user_id: botUserId, config };
};

export const useChatbotsStore = defineStore('chatbots', {
  state: () => ({
    chatbots: [],
    assignableBotUsers: [],
  }),
  getters: {
    list: state => state.chatbots,
  },
  actions: {
    async fetch() {
      const { data } = await ChatbotsAPI.get();
      this.chatbots = (data.payload || []).map(toViewModel);
    },
    // Bot users free to assign — excludes any already linked to another
    // chatbot. The dialog adds back the chatbot's own current bot user
    // (already on the chatbot object) when editing.
    async fetchAssignableBotUsers() {
      const { data } = await ChatbotsAPI.assignableBotUsers();
      this.assignableBotUsers = data.payload || [];
      return this.assignableBotUsers;
    },
    // The linked tenant's current bot configuration, for pre-filling the
    // dialog on open. `null`/empty fields when there's nothing to pre-fill
    // (no tenant linked, no config yet, or the lookup failed) — never an
    // error; the dialog just shows blank in that case, same as today.
    async fetchTenantBotConfig() {
      const { data } = await ChatbotsAPI.tenantBotConfig().catch(() => ({
        data: {},
      }));
      return data.payload || {};
    },
    async create(payload) {
      const { data } = await ChatbotsAPI.create(toPayload(payload));
      const bot = toViewModel(data.payload);
      this.chatbots = [bot, ...this.chatbots];
      this.dispatchEvent(bot, 'created');
      return bot;
    },
    async update(id, payload) {
      const { data } = await ChatbotsAPI.update(id, toPayload(payload));
      const bot = toViewModel(data.payload);
      this.chatbots = this.chatbots.map(b => (b.id === id ? bot : b));
      this.dispatchEvent(bot, 'updated');
      return bot;
    },
    async toggleActive(id) {
      const current = this.chatbots.find(b => b.id === id);
      if (!current) return;
      const { data } = await ChatbotsAPI.update(id, { active: !current.active });
      const bot = toViewModel(data.payload);
      this.chatbots = this.chatbots.map(b => (b.id === id ? bot : b));
      this.dispatchEvent(bot, bot.active ? 'enabled' : 'disabled');
    },
    async remove(id) {
      const bot = this.chatbots.find(b => b.id === id);
      await ChatbotsAPI.delete(id);
      this.chatbots = this.chatbots.filter(b => b.id !== id);
      if (bot) this.dispatchEvent(bot, 'removed');
    },
    // Let server-side custom automation rules react to chatbot lifecycle events.
    dispatchEvent(bot, eventType) {
      CustomAutomationsAPI.dispatch('chatbot_event', {
        bot_name: bot.botName || '',
        event_type: eventType,
        status: bot.active ? 'active' : 'inactive',
        botId: bot.id,
        agent_ids: [],
      }).catch(() => {});
    },
  },
});
